import * as Switch from '@wandb/weave/components/Switch';
import React from 'react';
import {twMerge} from 'tailwind-merge';

import {
  calculateIsSettingEnabled,
  SettingOverrideCounts,
} from '../../../../state/views/workspaceSettings/settingOverrideUtils';
import {
  ClearSettingRow,
  useClearSettingRows,
} from '../hooks/useClearSettingRows';
import {ClearSettingFnType} from '../hooks/useSettingOverrides';
import {LinePlotSettings, SettingLevel} from '../types';
import {ClearSettingsRows} from './ClearSettingsRow';
import {ResetSettingButton} from './ResetSettingButton';
import {SettingLabel} from './SettingLabel';

type SettingToggleRowProps = {
  clearSetting?: ClearSettingFnType;
  description?: string;
  /** Determines how label and description should be aligned with respect to one another */
  descriptionAlignment?: 'row' | 'column';
  displayValue: any;
  emptySettings?: Record<string, any>;
  label: string;
  numOverrides?: SettingOverrideCounts;
  onCheckedChange: (newValue: any) => void;
  settingKey?: string;
  settingLevel?: SettingLevel;
  value?: boolean | undefined;
};

export const SettingToggleRow = (props: SettingToggleRowProps & {}) => {
  const {clearSetting, emptySettings, numOverrides, settingKey, settingLevel} =
    props;
  const clearSettingRows = useClearSettingRows({
    clearSetting,
    emptySettings,
    numOverrides,
    settingKey,
    settingLevel,
  });

  if (
    props.descriptionAlignment == null ||
    props.descriptionAlignment === 'row'
  ) {
    return (
      <SettingToggleRowAligned clearSettingRows={clearSettingRows} {...props} />
    );
  }

  return (
    <SettingToggleColumnAligned
      clearSettingRows={clearSettingRows}
      {...props}
    />
  );
};

const SettingToggleRowAligned = ({
  clearSetting,
  clearSettingRows,
  description,
  displayValue,
  emptySettings,
  label,
  onCheckedChange,
  settingKey,
  settingLevel,
  value,
}: SettingToggleRowProps & {
  clearSettingRows: ClearSettingRow[];
}) => {
  return (
    <div className={twMerge('flex flex-col')}>
      <div
        className={twMerge(
          description == null ? 'flex justify-between' : 'flex items-center'
        )}>
        <SettingLabel
          text={label}
          className={twMerge(description == null ? 'grow' : '')}
        />
        <div
          className={twMerge(
            'flex items-center gap-8',
            description == null ? 'justify-end' : 'grow justify-between'
          )}>
          {description != null && (
            <p className={twMerge('text-base leading-[140%]', 'mr-16')}>
              {description}
            </p>
          )}
          <Switch.Root
            aria-label={label}
            checked={displayValue}
            onCheckedChange={onCheckedChange}>
            <Switch.Thumb checked={displayValue} />
          </Switch.Root>
          <SettingToggleResetSettingButton
            clearSetting={clearSetting}
            emptySettings={emptySettings}
            settingKey={settingKey}
            settingLevel={settingLevel}
            value={value}
          />
        </div>
      </div>
      <ClearSettingsRows rows={clearSettingRows} />
    </div>
  );
};

const SettingToggleColumnAligned = ({
  clearSetting,
  clearSettingRows,
  description,
  displayValue,
  emptySettings,
  label,
  onCheckedChange,
  settingKey,
  settingLevel,
  value,
}: SettingToggleRowProps & {
  clearSettingRows: ClearSettingRow[];
}) => {
  return (
    <div className={twMerge('flex flex-col')}>
      <SettingLabel text={label} className={twMerge('w-fit')} />
      <div className={twMerge('flex')}>
        {description != null && (
          <p className={twMerge('text-base leading-[140%]', 'mr-16')}>
            {description}
          </p>
        )}
        <Switch.Root
          aria-label={label}
          checked={displayValue}
          onCheckedChange={onCheckedChange}
          className="shrink-0">
          <Switch.Thumb checked={displayValue} />
        </Switch.Root>
        <SettingToggleResetSettingButton
          clearSetting={clearSetting}
          emptySettings={emptySettings}
          settingKey={settingKey}
          settingLevel={settingLevel}
          value={value}
        />
      </div>
      <ClearSettingsRows rows={clearSettingRows} />
    </div>
  );
};

const SettingToggleResetSettingButton = ({
  clearSetting,
  emptySettings = {},
  settingKey,
  settingLevel,
  value,
}: {
  clearSetting?: ClearSettingFnType;
  emptySettings?: LinePlotSettings;
  settingKey?: string;
  settingLevel?: SettingLevel;
  value?: boolean | undefined;
}) => {
  if (settingKey == null || settingLevel == null) {
    return null;
  }

  return (
    <ResetSettingButton
      isEnabled={calculateIsSettingEnabled(settingKey, {
        [settingKey]: value,
      })}
      onButtonClick={() => {
        clearSetting?.(settingLevel, settingKey, emptySettings);
      }}
      settingLevel={settingLevel}
    />
  );
};
